import React, { useState, useEffect } from 'react';
import { ChevronDown, Save, Clock, User, Building2, Calendar, IndianRupee } from 'lucide-react';
import { attendanceAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import { useDropdownData } from '../utils/useDropdownData';
import { useAuth, useActiveEstId } from './AuthContext';
import DateInput from './DateInput';
import './UpdateEmployee.css';

const OvertimeWork = () => {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  const [formData, setFormData] = useState({
    establishment: lockedEstId || '',
    employee: '',
    department: '',
    overtimeDate: new Date().toISOString().split('T')[0],
    startTime: '',
    endTime: '',
    overtimeType: 'weekday',
    hourlyRate: '',
    reason: '',
    status: 'approved'
  });

  // Separate state for time picker components
  const [startTimeComponents, setStartTimeComponents] = useState({
    hour: '09',
    minute: '00',
    period: 'AM'
  });

  const [endTimeComponents, setEndTimeComponents] = useState({
    hour: '05',
    minute: '00',
    period: 'PM'
  });

  // Use custom hook for dropdown data
  const {
    establishments,
    employees,
    loading,
    fetchEstablishments,
    fetchEmployeesByEstablishment,
    clearEmployees
  } = useDropdownData();

  // Fetch establishments on mount
  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (lockedEstId) {
      setFormData(prev => ({ ...prev, establishment: lockedEstId }));
      fetchEmployeesByEstablishment(lockedEstId);
    }
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lockedEstId) {
      fetchEmployeesByEstablishment(lockedEstId); // ClientAdmin: load their employees
    } else {
      fetchEstablishments();
    }
  }, [fetchEstablishments]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update formData.startTime when startTimeComponents change
  useEffect(() => {
    const timeString = `${startTimeComponents.hour}:${startTimeComponents.minute} ${startTimeComponents.period}`;
    setFormData(prev => ({ ...prev, startTime: timeString }));
  }, [startTimeComponents]);

  // Update formData.endTime when endTimeComponents change
  useEffect(() => {
    const timeString = `${endTimeComponents.hour}:${endTimeComponents.minute} ${endTimeComponents.period}`;
    setFormData(prev => ({ ...prev, endTime: timeString }));
  }, [endTimeComponents]);

  const handleStartTimeChange = (field, value) => {
    setStartTimeComponents(prev => ({ ...prev, [field]: value }));
  };

  const handleEndTimeChange = (field, value) => {
    setEndTimeComponents(prev => ({ ...prev, [field]: value }));
  };

  const overtimeTypes = [
    { value: 'weekday', label: 'Weekday Overtime (1.5x)', multiplier: 1.5 },
    { value: 'weekend', label: 'Weekend Overtime (2x)', multiplier: 2.0 },
    { value: 'holiday', label: 'Holiday Overtime (2.5x)', multiplier: 2.5 },
    { value: 'night', label: 'Night Shift Overtime (1.75x)', multiplier: 1.75 }
  ];

  const statusOptions = [
    { value: 'approved', label: 'Approved' },
    { value: 'paid', label: 'Paid' }
  ];

  const calculateOvertimeHours = () => {
    if (formData.startTime && formData.endTime) {
      // Parse time string in format "h:mm a" (e.g., "9:30 AM" or "2:15 PM")
      const parseTime = (timeStr) => {
        if (!timeStr) return null;

        // Handle both 24-hour format (HH:mm) and 12-hour format (h:mm a)
        if (timeStr.includes('AM') || timeStr.includes('PM') || timeStr.includes('am') || timeStr.includes('pm')) {
          const [time, period] = timeStr.split(' ');
          const [hours, minutes] = time.split(':').map(Number);
          let hour24 = hours;

          if (period.toUpperCase() === 'PM' && hours !== 12) {
            hour24 = hours + 12;
          } else if (period.toUpperCase() === 'AM' && hours === 12) {
            hour24 = 0;
          }

          return hour24 * 60 + minutes;
        } else {
          const [hours, minutes] = timeStr.split(':').map(Number);
          return hours * 60 + minutes;
        }
      };

      const startMinutes = parseTime(formData.startTime);
      const endMinutes = parseTime(formData.endTime);

      if (startMinutes === null || endMinutes === null) return '0.00';

      let totalMinutes = endMinutes - startMinutes;

      // Handle overnight overtime
      if (totalMinutes < 0) {
        totalMinutes += 24 * 60;
      }

      return (totalMinutes / 60).toFixed(2);
    }
    return '0.00';
  };

  const calculateOvertimePay = () => {
    const hours = parseFloat(calculateOvertimeHours());
    const baseRate = parseFloat(formData.hourlyRate) || 0;
    const multiplier = overtimeTypes.find(t => t.value === formData.overtimeType)?.multiplier || 1.5;

    return (hours * baseRate * multiplier).toFixed(2);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEstablishmentChange = (value) => {
    setFormData(prev => ({
      ...prev,
      establishment: value,
      employee: '',
      department: '',
      hourlyRate: ''
    }));

    // Clear previous employees and fetch new ones
    clearEmployees();
    if (value) {
      fetchEmployeesByEstablishment(value);
    }
  };

  const handleEmployeeChange = (value) => {
    const selectedEmp = employees.find(e => e.employee_id === value);
    console.log('Selected employee data:', selectedEmp);

    // Use ot_hrly_pay if available, otherwise calculate from basic_salary or basic or salary
    let hourlyRate = '';
    if (selectedEmp?.ot_hrly_pay) {
      hourlyRate = parseFloat(selectedEmp.ot_hrly_pay).toFixed(2);
    } else if (selectedEmp?.basic) {
      hourlyRate = (selectedEmp.basic / 160).toFixed(2);
    } else if (selectedEmp?.salary) {
      hourlyRate = (selectedEmp.salary / 160).toFixed(2);
    } else if (selectedEmp?.basic_salary) {
      hourlyRate = (selectedEmp.basic_salary / 160).toFixed(2);
    }

    console.log('Calculated hourly rate:', hourlyRate);

    setFormData(prev => ({
      ...prev,
      employee: value,
      department: selectedEmp?.department || '',
      hourlyRate: hourlyRate
    }));
  };

  const handleSubmit = async () => {
    if (!formData.establishment || !formData.employee || !formData.overtimeDate) {
      alert('Please fill in required fields');
      return;
    }

    try {
      // Convert 12-hour format to 24-hour format for API
      const convertTo24Hour = (timeStr) => {
        if (!timeStr) return '00:00';

        if (timeStr.includes('AM') || timeStr.includes('PM') || timeStr.includes('am') || timeStr.includes('pm')) {
          const [time, period] = timeStr.split(' ');
          const [hours, minutes] = time.split(':').map(Number);
          let hour24 = hours;

          if (period.toUpperCase() === 'PM' && hours !== 12) {
            hour24 = hours + 12;
          } else if (period.toUpperCase() === 'AM' && hours === 12) {
            hour24 = 0;
          }

          return `${String(hour24).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        }

        return timeStr;
      };

      const overtimeData = {
        establishmentId: parseInt(formData.establishment) || 1,
        employeeId: formData.employee,
        department: formData.department || 'N/A',
        overtimeDate: formData.overtimeDate,
        startTime: convertTo24Hour(formData.startTime),
        endTime: convertTo24Hour(formData.endTime),
        overtimeType: formData.overtimeType,
        hourlyRate: formData.hourlyRate ? parseFloat(formData.hourlyRate) : null,
        totalHours: parseFloat(calculateOvertimeHours()),
        totalAmount: parseFloat(calculateOvertimePay()),
        reason: formData.reason,
        status: formData.status || 'approved'
      };

      await attendanceAPI.addOvertime(overtimeData);
      notifySavedAndReload('✓ Overtime work logged successfully!');

      // Reset form
      setFormData({
        establishment: '',
        employee: '',
        department: '',
        overtimeDate: new Date().toISOString().split('T')[0],
        startTime: '',
        endTime: '',
        overtimeType: 'weekday',
        hourlyRate: '',
        reason: '',
        status: 'approved'
      });
      setStartTimeComponents({ hour: '09', minute: '00', period: 'AM' });
      setEndTimeComponents({ hour: '05', minute: '00', period: 'PM' });
      clearEmployees();
    } catch (error) {
      console.error('Error logging overtime:', error);
      alert('❌ Error logging overtime: ' + error.message);
    }
  };

  return (
    <div className="update-employee-container">
      <div className="update-employee-wrapper">
        <div className="update-employee-header">
          <h2 className="update-employee-title">Overtime Work</h2>
          <p className="update-employee-subtitle">Record and manage employee overtime work hours</p>
        </div>

        {/* Selection Card */}
        <div className="update-employee-dropdown-card">
          <div className="update-employee-dropdown-group">
            {/* Establishment Dropdown — hidden for ClientAdmin */}
            {!lockedEstId && (
            <div className="update-employee-field">
              <div className="update-employee-dropdown">
                <select
                  value={formData.establishment}
                  onChange={(e) => handleEstablishmentChange(e.target.value)}
                  className="update-employee-select"
                  disabled={loading.establishments}
                >
                  <option value="">
                    {loading.establishments ? 'Loading...' : 'Select Organization'}
                  </option>
                  {establishments.map((est) => (
                    <option key={est.id} value={est.id}>{est.name}</option>
                  ))}
                </select>
                <ChevronDown className="update-employee-chevron" size={18} />
              </div>
            </div>
            )}

            {/* Employee Dropdown */}
            {formData.establishment && (
              <div className="update-employee-field animate-fadeIn">
                <div className="update-employee-dropdown">
                  <select
                    value={formData.employee}
                    onChange={(e) => handleEmployeeChange(e.target.value)}
                    className="update-employee-select"
                    disabled={loading.employees}
                  >
                    <option value="">
                      {loading.employees ? 'Loading...' : 'Select Employee'}
                    </option>
                    {employees.map(emp => (
                      <option key={emp.employee_id} value={emp.employee_id}>
                        {emp.first_name} {emp.last_name} ({emp.employee_id}) - {emp.department || 'N/A'}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="update-employee-chevron" size={18} />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Form Content */}
        {formData.employee && (
          <div className="update-employee-form animate-fadeIn" style={{marginTop: '1.5rem'}}>
            <div className="update-employee-content">
              <div className="update-employee-section-header">Overtime Details</div>
              <div className="update-employee-section-description">Enter overtime work information and calculate overtime pay</div>

              <div className="update-employee-form-grid">
                {/* Overtime Date */}
                <div className="update-employee-form-group">
                  <label className="update-employee-form-label">Overtime Date</label>
                  <DateInput
                    value={formData.overtimeDate}
                    onChange={(e) => handleInputChange('overtimeDate', e.target.value)}
                    className="update-employee-input"
                  />
                </div>

                {/* Overtime Type */}
                <div className="update-employee-form-group">
                  <label className="update-employee-form-label">Overtime Type</label>
                  <div className="update-employee-dropdown">
                    <select
                      value={formData.overtimeType}
                      onChange={(e) => handleInputChange('overtimeType', e.target.value)}
                      className="update-employee-form-select"
                    >
                      {overtimeTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                    <ChevronDown className="update-employee-chevron" size={18} />
                  </div>
                </div>

                {/* Start Time */}
                <div className="update-employee-form-group">
                  <label className="update-employee-form-label">Start Time</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
                    <div className="update-employee-dropdown">
                      <select
                        value={startTimeComponents.hour}
                        onChange={(e) => handleStartTimeChange('hour', e.target.value)}
                        className="update-employee-form-select"
                      >
                        {['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'].map(h => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                      <ChevronDown className="update-employee-chevron" size={16} />
                    </div>
                    <div className="update-employee-dropdown">
                      <select
                        value={startTimeComponents.minute}
                        onChange={(e) => handleStartTimeChange('minute', e.target.value)}
                        className="update-employee-form-select"
                      >
                        {['00', '15', '30', '45'].map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                      <ChevronDown className="update-employee-chevron" size={16} />
                    </div>
                    <div className="update-employee-dropdown">
                      <select
                        value={startTimeComponents.period}
                        onChange={(e) => handleStartTimeChange('period', e.target.value)}
                        className="update-employee-form-select"
                      >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                      <ChevronDown className="update-employee-chevron" size={16} />
                    </div>
                  </div>
                </div>

                {/* End Time */}
                <div className="update-employee-form-group">
                  <label className="update-employee-form-label">End Time</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
                    <div className="update-employee-dropdown">
                      <select
                        value={endTimeComponents.hour}
                        onChange={(e) => handleEndTimeChange('hour', e.target.value)}
                        className="update-employee-form-select"
                      >
                        {['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'].map(h => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                      <ChevronDown className="update-employee-chevron" size={16} />
                    </div>
                    <div className="update-employee-dropdown">
                      <select
                        value={endTimeComponents.minute}
                        onChange={(e) => handleEndTimeChange('minute', e.target.value)}
                        className="update-employee-form-select"
                      >
                        {['00', '15', '30', '45'].map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                      <ChevronDown className="update-employee-chevron" size={16} />
                    </div>
                    <div className="update-employee-dropdown">
                      <select
                        value={endTimeComponents.period}
                        onChange={(e) => handleEndTimeChange('period', e.target.value)}
                        className="update-employee-form-select"
                      >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                      <ChevronDown className="update-employee-chevron" size={16} />
                    </div>
                  </div>
                </div>

                {/* Overtime Hours Calculation */}
                {formData.startTime && formData.endTime && (
                  <div className="update-employee-form-group animate-fadeIn">
                    <label className="update-employee-form-label">Total Overtime Hours</label>
                    <div style={{
                      padding: '0.625rem 1rem',
                      background: 'var(--theme-accent)',
                      border: '1.5px solid var(--theme-primary)',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: 'var(--theme-primary)'
                    }}>
                      {calculateOvertimeHours()} hours
                    </div>
                  </div>
                )}

                {/* Hourly Rate */}
                <div className="update-employee-form-group">
                  <label className="update-employee-form-label">Base Hourly Rate (Rs)</label>
                  <input
                    type="number"
                    value={formData.hourlyRate}
                    onChange={(e) => handleInputChange('hourlyRate', e.target.value)}
                    className="update-employee-input"
                    placeholder="Enter hourly rate"
                    min="0"
                    step="0.01"
                  />
                </div>

                {/* Overtime Pay Calculation */}
                {formData.startTime && formData.endTime && formData.hourlyRate && (
                  <div className="update-employee-form-group animate-fadeIn">
                    <label className="update-employee-form-label">Overtime Pay (Calculated)</label>
                    <div style={{
                      padding: '0.625rem 1rem',
                      background: '#f0fdf4',
                      border: '1.5px solid #10b981',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#059669'
                    }}>
                      Rs {calculateOvertimePay()}
                    </div>
                  </div>
                )}

                {/* Status */}
                <div className="update-employee-form-group">
                  <label className="update-employee-form-label">Status</label>
                  <div className="update-employee-dropdown">
                    <select
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      className="update-employee-form-select"
                    >
                      {statusOptions.map(status => (
                        <option key={status.value} value={status.value}>{status.label}</option>
                      ))}
                    </select>
                    <ChevronDown className="update-employee-chevron" size={18} />
                  </div>
                </div>

                {/* Reason */}
                <div className="update-employee-form-group" style={{gridColumn: '1 / -1'}}>
                  <label className="update-employee-form-label">Reason for Overtime</label>
                  <textarea
                    value={formData.reason}
                    onChange={(e) => handleInputChange('reason', e.target.value)}
                    rows="3"
                    placeholder="Enter reason for overtime work"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="update-employee-actions">
                <button onClick={handleSubmit} className="update-employee-btn-create">
                  <Save size={16} />
                  Save Overtime Record
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OvertimeWork;
